import { PageSize } from '@/constant';
import {
	GetWorkerApplicantListsDetailParams,
	GetWorkerRecommenderListParams,
	IRecommenderListItem,
	IWorkerRecommenderItem,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '..';
import {
	formatWorkerRecommenderApplicantList,
	formatWorkerRecommenderDetail,
	formatWorkerRecommenderList,
} from './worker-recommender.helper';
import {
	IPayloadCreateWorkerRecommenderApplicantRequest,
	IPayloadCreateWorkerRecommenderRequest,
	IPayloadDeleteWorkerRecommenderApplicantRequest,
	IPayloadDeleteWorkerRecommenderLodgingByIdRequest,
	IPayloadDeleteWorkerRecommendersRequest,
	IPayloadGetWorkerRecommenderApplicantListRequest,
	IPayloadGetWorkerRecommenderApplicantListSuccess,
	IPayloadGetWorkerRecommenderDetailRequest,
	IPayloadGetWorkerRecommenderDetailSuccess,
	IPayloadGetWorkerRecommenderExcelListRequest,
	IPayloadGetWorkerRecommenderListRequest,
	IPayloadGetWorkerRecommenderListSuccess,
	IPayloadGetWorkerRecommenderLodgingListRequest,
	IPayloadGetWorkerRecommenderLodgingListSuccess,
	IWorkerRecommenderApplicantDetailStore,
	IWorkerRecommenderDetailStore,
	IWorkerRecommenderListStore,
	IWorkerRecommenderLodgingListStore,
	TPayloadUploadExcelRecommenderRequest,
	TPayloadUploadExcelRecommenderSuccess,
} from './worker-recommender.type';

const InitParams: GetWorkerRecommenderListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const InitApplicantListParams = {
	page: 1,
	pageSize: PageSize.S,
};

const InitListsDetailParams: GetWorkerApplicantListsDetailParams = {
	page: 1,
	pageSize: PageSize.S,
	id: 0,
};

const initialState = {
	fetching: false,
	excelListFetching: false,
	workerRecommenderList: [] as IWorkerRecommenderListStore[],
	params: InitParams,
	total: 0,
	workerRecommenderDetail: {} as IWorkerRecommenderDetailStore,
	paramsListsDetail: InitListsDetailParams,

	applicantList: [] as IWorkerRecommenderApplicantDetailStore[],
	applicantListParams: InitApplicantListParams,
	applicantListTotal: 0,

	allApplicantList: [] as IWorkerRecommenderApplicantDetailStore[],

	recommenderUploadFailedList:
		[] as TPayloadUploadExcelRecommenderSuccess['failedList'],

	uploadExcelResult: {
		successCount: 0,
		failCount: 0,
	},
	workerRecommenderLodgingList: [] as IWorkerRecommenderLodgingListStore[],
};

const workerRecommenderSlice = createSlice({
	initialState,
	name: 'workerRecommender',
	reducers: {
		/**---- GET WORKER_RECOMMENDER LIST START ---- */
		getWorkerRecommenderListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerRecommenderListRequest>
		) => {
			state.fetching = true;
			state.params = action?.payload;
		},
		getWorkerRecommenderListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerRecommenderListSuccess>
		) => {
			const { workerRecommenderList, total } = action.payload;
			state.fetching = false;
			state.workerRecommenderList = workerRecommenderList;
			state.total = total;
		},
		getWorkerRecommenderListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.workerRecommenderList = [];
		},

		getWorkerRecommenderExcelListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerRecommenderExcelListRequest>
		) => {
			state.excelListFetching = true;
		},
		getWorkerRecommenderExcelListSuccess: (state) => {
			state.excelListFetching = false;
		},
		getWorkerRecommenderExcelListFailure: (state) => {
			state.excelListFetching = false;
		},
		/**---- GET WORKER_RECOMMENDER LIST END ---- */

		/**---- GET RECOMMENDER LODGING START ---- */
		getWorkerRecommenderLodgingListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerRecommenderLodgingListRequest>
		) => {
			state.fetching = true;
			state.paramsListsDetail = action?.payload;
		},
		getWorkerRecommenderLodgingListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerRecommenderLodgingListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.workerRecommenderLodgingList = content;
			state.total = total;
		},
		getWorkerRecommenderLodgingListFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET RECOMMENDER LODGING START ---- */

		/**---- DELETE RECOMMENDER LODGING START ---- */
		deleteWorkerRecommenderLodgingRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerRecommenderLodgingByIdRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerRecommenderLodgingSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerRecommenderLodgingFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE RECOMMENDER LODGING END ---- */

		/**---- GET WORKER_RECOMMENDER DETAIL START ---- */
		getWorkerRecommenderDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerRecommenderDetailRequest>
		) => {
			state.fetching = true;
		},
		getWorkerRecommenderDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerRecommenderDetailSuccess>
		) => {
			const { workerRecommenderDetail } = action.payload;
			state.fetching = false;
			state.workerRecommenderDetail = workerRecommenderDetail;
		},
		getWorkerRecommenderDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET WORKER_RECOMMENDER DETAIL END ---- */

		/**---- UPDATE WORKER_RECOMMENDER START ---- */
		updateWorkerRecommenderSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IRecommenderListItem>>
		) => {
			const { data } = action.payload;
			const workerRecommenderList = [...state.workerRecommenderList];

			state.workerRecommenderList = formatWorkerRecommenderList(
				mergeListItemUpdated<IWorkerRecommenderListStore, IRecommenderListItem>(
					workerRecommenderList,
					data
				)
			);
		},
		updateWorkerRecommenderDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IWorkerRecommenderItem>>
		) => {
			const { data } = action.payload;
			const workerRecommenderDetail = { ...state.workerRecommenderDetail };
			state.workerRecommenderDetail = formatWorkerRecommenderDetail(
				mergeItemUpdated<IWorkerRecommenderDetailStore, IWorkerRecommenderItem>(
					workerRecommenderDetail,
					data
				)
			);
		},
		updateWorkerRecommenderApplicantSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IRecommenderListItem>>
		) => {
			const { data } = action.payload;
			const workerRecommenderApplicantList = [...state.applicantList];
			state.applicantList = formatWorkerRecommenderApplicantList(
				mergeListItemUpdated<
					IWorkerRecommenderApplicantDetailStore,
					IRecommenderListItem
				>(workerRecommenderApplicantList, data)
			);
		},
		/**---- UPDATE WORKER_RECOMMENDER END ---- */

		/**---- DELETE WORKER_RECOMMENDER START ---- */
		deleteWorkerRecommendersRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerRecommendersRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerRecommendersSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerRecommendersFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER_RECOMMENDER END ---- */

		/**---- CREATE WORKER_RECOMMENDER START ---- */
		createWorkerRecommenderRequest: (
			state,
			action: PayloadAction<IPayloadCreateWorkerRecommenderRequest>
		) => {
			state.fetching = true;
		},
		createWorkerRecommenderSuccess: (state) => {
			state.fetching = false;
		},
		createWorkerRecommenderFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE WORKER_RECOMMENDER END ---- */

		/**---- GET RECOMMENDER APPLICANT START ---- */
		getWorkerRecommenderApplicantListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerRecommenderApplicantListRequest>
		) => {
			state.fetching = true;
			state.applicantListParams = action?.payload;
		},
		getWorkerRecommenderApplicantListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerRecommenderApplicantListSuccess>
		) => {
			const { applicantList, applicantTotal } = action.payload;
			state.fetching = false;
			state.applicantList = applicantList;
			state.applicantListTotal = applicantTotal;
		},
		getWorkerRecommenderApplicantListFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET RECOMMENDER APPLICANT END ---- */

		/**---- GET ALL RECOMMENDER APPLICANT START ---- */
		getAllWorkerRecommenderApplicantListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerRecommenderApplicantListRequest>
		) => {
			state.fetching = true;
		},
		getAllWorkerRecommenderApplicantListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerRecommenderApplicantListSuccess>
		) => {
			const { applicantList } = action.payload;
			state.fetching = false;
			state.allApplicantList = applicantList;
		},
		getAllWorkerRecommenderApplicantListFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET RECOMMENDER APPLICANT END ---- */

		/**---- DELETE RECOMMENDER APPLICANT START ---- */
		deleteWorkerRecommenderApplicantRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerRecommenderApplicantRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerRecommenderApplicantSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerRecommenderApplicantFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE RECOMMENDER APPLICANT END ---- */

		/**---- CREATE RECOMMENDER APPLICANT START ---- */
		createWorkerRecommenderApplicantRequest: (
			state,
			action: PayloadAction<IPayloadCreateWorkerRecommenderApplicantRequest>
		) => {
			state.fetching = true;
		},
		createWorkerRecommenderApplicantSuccess: (state) => {
			state.fetching = false;
		},
		createWorkerRecommenderApplicantFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE RECOMMENDER APPLICANT END ---- */

		/**---- UPLOAD EXCEL RECOMMENDER START ---- */
		uploadExcelRecommenderRequest: (
			state,
			action: PayloadAction<TPayloadUploadExcelRecommenderRequest>
		) => {
			state.fetching = true;
			state.recommenderUploadFailedList = [];
			state.uploadExcelResult = {
				successCount: 0,
				failCount: 0,
			};
		},
		uploadExcelRecommenderSuccess: (
			state,
			action: PayloadAction<TPayloadUploadExcelRecommenderSuccess>
		) => {
			state.fetching = false;
			state.recommenderUploadFailedList = action.payload.failedList;
			state.uploadExcelResult = {
				successCount: action.payload.successCount || 0,
				failCount: action.payload.failCount || 0,
			};
		},
		uploadExcelRecommenderFailure: (state) => {
			state.fetching = false;
			state.uploadExcelResult = {
				successCount: 0,
				failCount: 0,
			};
		},
		/**---- UPLOAD EXCEL RECOMMENDER END ---- */
	},
});

export const WorkerRecommenderSelectors = {
	selectFetching: (state: RootState) => state.workerRecommender.fetching,
	selectTotal: (state: RootState) => state.workerRecommender.total,
	selectWorkerRecommenderList: (state: RootState) =>
		state.workerRecommender.workerRecommenderList,
	selectWorkerRecommenderDetail: (state: RootState) =>
		state.workerRecommender.workerRecommenderDetail,
	selectParams: (state: RootState) => state.workerRecommender.params,
	selectParamsListsDetail: (state: RootState) =>
		state.workerRecommender.paramsListsDetail,

	selectApplicantList: (state: RootState) =>
		state.workerRecommender.applicantList,
	selectApplicantListParams: (state: RootState) =>
		state.workerRecommender.applicantListParams,
	selectRecommenderLodgingList: (state: RootState) =>
		state.workerRecommender.workerRecommenderLodgingList,
	selectFailedList: (state: RootState) =>
		state.workerRecommender.recommenderUploadFailedList,

	selectAllApplicantList: (state: RootState) =>
		state.workerRecommender.allApplicantList,
	selectUploadExcelResult: (state: RootState) =>
		state.workerRecommender.uploadExcelResult,
	selectTotalApplicantList: (state: RootState) =>
		state.workerRecommender.applicantListTotal,
};

export const WorkerRecommenderActions = workerRecommenderSlice.actions;
export const WorkerRecommenderReducer = workerRecommenderSlice.reducer;
